/**
 * WCAG 2.1 contrast ratio calculation utilities.
 *
 * Implements the algorithms defined in:
 * - https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * - https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 */

export interface RGB {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

/**
 * Converts a hex color string to an RGB object.
 * Supports 3-character (#abc) and 6-character (#aabbcc) hex formats.
 */
export function hexToRgb(hex: string): RGB {
  const cleaned = hex.replace(/^#/, '');

  let r: number;
  let g: number;
  let b: number;

  if (cleaned.length === 3) {
    r = parseInt(cleaned[0]! + cleaned[0]!, 16);
    g = parseInt(cleaned[1]! + cleaned[1]!, 16);
    b = parseInt(cleaned[2]! + cleaned[2]!, 16);
  } else if (cleaned.length === 6) {
    r = parseInt(cleaned.slice(0, 2), 16);
    g = parseInt(cleaned.slice(2, 4), 16);
    b = parseInt(cleaned.slice(4, 6), 16);
  } else {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  return { r, g, b };
}

/**
 * Computes relative luminance per WCAG 2.1 definition.
 *
 * For each sRGB channel value (0–255):
 * 1. Normalize to 0–1 range
 * 2. Apply linearization:
 *    - If value <= 0.04045: linear = value / 12.92
 *    - Else: linear = ((value + 0.055) / 1.055) ^ 2.4
 * 3. L = 0.2126 * R_lin + 0.7152 * G_lin + 0.0722 * B_lin
 */
export function relativeLuminance(rgb: RGB): number {
  const linearize = (channel: number): number => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };

  const rLin = linearize(rgb.r);
  const gLin = linearize(rgb.g);
  const bLin = linearize(rgb.b);

  return 0.2126 * rLin + 0.7152 * gLin + 0.0722 * bLin;
}

/**
 * Computes the contrast ratio between two hex colors per WCAG 2.1.
 *
 * Contrast ratio = (L1 + 0.05) / (L2 + 0.05)
 * where L1 is the lighter luminance and L2 is the darker luminance.
 *
 * Returns a value between 1:1 (no contrast) and 21:1 (maximum contrast).
 */
export function contrastRatio(color1: string, color2: string): number {
  const lum1 = relativeLuminance(hexToRgb(color1));
  const lum2 = relativeLuminance(hexToRgb(color2));

  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);

  return (lighter + 0.05) / (darker + 0.05);
}
